import { useState, useEffect } from 'react';
import { Dialog, HowItWorks, Loading, api, useToast } from './_barrel.js';

const TEAM_ROLES = ['admin', 'lead_analyst', 'analyst'];
const ROLE_RANK = { analyst: 0, lead_analyst: 1, admin: 2 };
const ROLE_LABEL = { admin: 'Admin', lead_analyst: 'Lead Analyst', analyst: 'Analyst' };

        function SettingsView({ token, me }) {
            const [tenant, setTenant] = useState(null);
            const [smtp, setSmtp] = useState(null);
            const [error, setError] = useState(null);
            const [savingTenant, setSavingTenant] = useState(false);
            const [refresh, setRefresh] = useState(0);
            const toast = useToast();

            const myRole = me?.analyst?.role;
            const isAdmin = myRole === 'admin';
            const myRank = ROLE_RANK[myRole] ?? -1;
            // Roles this tenant admin is allowed to grant/hold — never above their own.
            const grantableRoles = TEAM_ROLES.filter(r => ROLE_RANK[r] <= myRank);

            // Team panel state
            const [team, setTeam] = useState([]);
            const [teamLoading, setTeamLoading] = useState(false);
            const [teamError, setTeamError] = useState(null);
            const [inviteOpen, setInviteOpen] = useState(false);
            const [inviteForm, setInviteForm] = useState({ full_name: '', email: '', role: 'analyst' });
            const [inviteBusy, setInviteBusy] = useState(false);
            const [inviteError, setInviteError] = useState(null);
            const [rowBusyId, setRowBusyId] = useState(null);

            const loadTeam = async () => {
                if (!isAdmin) return;
                setTeamLoading(true); setTeamError(null);
                try {
                    const r = await api('/api/v1/team/analysts', {}, token);
                    setTeam(r.items || []);
                } catch (e) { setTeamError(e.message); }
                finally { setTeamLoading(false); }
            };

            useEffect(() => { loadTeam(); }, [token, isAdmin]);

            const invite = async (e) => {
                e.preventDefault();
                if (!inviteForm.email || !inviteForm.full_name) { setInviteError('Name and email are required'); return; }
                setInviteBusy(true); setInviteError(null);
                try {
                    const r = await api('/api/v1/team/analysts/invite', {
                        method: 'POST', body: JSON.stringify(inviteForm),
                    }, token);
                    toast(r.message || 'Invitation sent');
                    setInviteForm({ full_name: '', email: '', role: 'analyst' });
                    setInviteOpen(false);
                    loadTeam();
                } catch (e) { setInviteError(e.message); }
                finally { setInviteBusy(false); }
            };

            const changeRole = async (a, role) => {
                if (role === a.role) return;
                setRowBusyId(a.id);
                try {
                    await api(`/api/v1/team/analysts/${a.id}`, {
                        method: 'PATCH', body: JSON.stringify({ role }),
                    }, token);
                    toast(`${a.display_name}: role set to ${ROLE_LABEL[role] || role}`);
                    loadTeam();
                } catch (e) { setTeamError(e.message); }
                finally { setRowBusyId(null); }
            };

            const toggleActive = async (a) => {
                const goingInactive = a.is_active;
                if (goingInactive && !confirm(`Deactivate ${a.display_name}? They will no longer be able to log in.`)) return;
                setRowBusyId(a.id);
                try {
                    await api(`/api/v1/team/analysts/${a.id}`, {
                        method: 'PATCH', body: JSON.stringify({ is_active: !a.is_active }),
                    }, token);
                    loadTeam();
                } catch (e) { setTeamError(e.message); }
                finally { setRowBusyId(null); }
            };

            // Tenant form
            const [displayName, setDisplayName] = useState('');
            const [legalName, setLegalName] = useState('');
            const [intakeEmail, setIntakeEmail] = useState('');
            const [assistProvider, setAssistProvider] = useState('');
            const [assistPhone, setAssistPhone] = useState('');
            const [medevacNote, setMedevacNote] = useState('');
            const [travelOnlyDays, setTravelOnlyDays] = useState('90');
            const [alertRadiusKm, setAlertRadiusKm] = useState('2');
            const [offItineraryKm, setOffItineraryKm] = useState('25');

            // SMTP test
            const [testTo, setTestTo] = useState(me?.analyst?.email || '');
            const [testing, setTesting] = useState(false);
            const [testResult, setTestResult] = useState(null);

            // Drain
            const [draining, setDraining] = useState(false);
            const [drainResult, setDrainResult] = useState(null);

            useEffect(() => {
                (async () => {
                    try {
                        const [t, s] = await Promise.all([
                            api('/api/v1/tenants/me', {}, token),
                            api('/api/v1/notifications/smtp-status', {}, token),
                        ]);
                        setTenant(t);
                        setSmtp(s);
                        setDisplayName(t.display_name || '');
                        setLegalName(t.legal_name || '');
                        setIntakeEmail(t.intake_email || '');
                        setAssistProvider(t.travel_assistance_provider || '');
                        setAssistPhone(t.travel_assistance_phone || '');
                        setMedevacNote(t.travel_medevac_note || '');
                        setTravelOnlyDays(String(t.travel_only_retention_days ?? 90));
                        setAlertRadiusKm(String(t.trip_alert_incident_radius_km ?? 2));
                        setOffItineraryKm(String(t.trip_alert_off_itinerary_km ?? 25));
                    } catch (e) { setError(e.message); }
                })();
            }, [token, refresh]);

            const saveTenant = async () => {
                const days = parseInt(travelOnlyDays, 10);
                if (!(days >= 7 && days <= 730)) { toast('Travel-only retention must be 7 to 730 days', 'error'); return; }
                const rKm = parseFloat(alertRadiusKm), oKm = parseFloat(offItineraryKm);
                if (!(rKm >= 0.2 && rKm <= 50)) { toast('Incident radius must be 0.2 to 50 km', 'error'); return; }
                if (!(oKm >= 1 && oKm <= 500)) { toast('Off-itinerary distance must be 1 to 500 km', 'error'); return; }
                setSavingTenant(true); setError(null);
                try {
                    await api('/api/v1/tenants/me', {
                        method: 'PATCH',
                        body: JSON.stringify({
                            display_name: displayName || null,
                            legal_name: legalName || null,
                            intake_email: intakeEmail || null,
                            travel_assistance_provider: assistProvider,
                            travel_assistance_phone: assistPhone,
                            travel_medevac_note: medevacNote,
                            travel_only_retention_days: days,
                            trip_alert_incident_radius_km: rKm,
                            trip_alert_off_itinerary_km: oKm,
                        }),
                    }, token);
                    toast('Tenant settings saved');
                    setRefresh(r => r+1);
                } catch (e) { setError(e.message); }
                finally { setSavingTenant(false); }
            };

            const sendTest = async () => {
                setTesting(true); setTestResult(null);
                try {
                    const r = await api('/api/v1/notifications/test-email', {
                        method: 'POST',
                        body: JSON.stringify({ to_address: testTo }),
                    }, token);
                    setTestResult(r);
                    if (r.sent) toast('Test sent');
                } catch (e) { setTestResult({ sent: false, reason: e.message }); }
                finally { setTesting(false); }
            };

            const drainNow = async () => {
                if (!confirm('Send all queued notifications now?')) return;
                setDraining(true); setDrainResult(null);
                try {
                    const r = await api('/api/v1/notifications/drain', { method: 'POST' }, token);
                    setDrainResult(r);
                    if (r.sent > 0) toast(`Sent ${r.sent}`);
                } catch (e) { setDrainResult({ reason: e.message }); }
                finally { setDraining(false); }
            };

            if (error) return <div className="max-w-5xl mx-auto px-6 py-8 text-[var(--red)]">{error}</div>;
            if (!tenant || !smtp) return <Loading />;

            return (
                <div className="max-w-5xl mx-auto px-6 py-10">
                    <div className="mb-8">
                        <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Configuration</div>
                        <h2 className="font-display text-4xl gold">Settings</h2>
                    </div>

                    <HowItWorks id="settings" title="How Settings works"
                        intro={'Settings is your tenant configuration — the identity and alert plumbing behind every report and notification. Changes here apply across the whole workspace.'}
                        steps={[
                            '<b>Tenant</b> — your display and legal name appear on every PDF report; the intake email is the default destination for alerts when a subscription does not specify its own.',
                            '<b>Team</b> (admins only) — invite an analyst by email and starting role, see who has an account and whether they are active, and change a colleague\'s role or deactivate them. You can\'t grant a role above your own, and you can\'t deactivate yourself.',
                            '<b>Outbound email</b> — check the SMTP status and send a test to confirm alerts and reports will actually reach recipients.',
                            'Save any change and it takes effect immediately for new reports and notifications.',
                        ]} />

                    {/* Tenant settings */}
                    <div className="panel mb-6">
                        <div className="panel-header">
                            <div className="font-display text-xl gold">Tenant</div>
                            <div className="text-xs font-mono text-muted mt-1">
                                Display + legal name appear on PDF reports. Intake email is the default
                                alert destination when a subscription doesn't specify its own.
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Display Name</label>
                                <input value={displayName} onChange={e => setDisplayName(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Legal Name (optional)</label>
                                <input value={legalName} onChange={e => setLegalName(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Intake Email (default alert destination)</label>
                                <input value={intakeEmail} onChange={e => setIntakeEmail(e.target.value)}
                                       placeholder="csit@yourcompany.com" />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Travel assistance provider (shown in trip packages)</label>
                                <input value={assistProvider} onChange={e => setAssistProvider(e.target.value)} placeholder="Provider name" />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Assistance 24-hour number</label>
                                <input value={assistPhone} onChange={e => setAssistPhone(e.target.value)} placeholder="+44 ..." />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Medevac note</label>
                                <input value={medevacNote} onChange={e => setMedevacNote(e.target.value)} placeholder="Who authorises evacuation, policy number reference, etc." />
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Travel-only report retention (days)</label>
                                <input type="number" min="7" max="730" value={travelOnlyDays} onChange={e => setTravelOnlyDays(e.target.value)} />
                                <div className="text-xs text-dim mt-1">Days after a travel-only trip ends before the traveller's name, party, medical details and contacts are erased automatically (7 to 730; default 90). The report itself is kept with "[erased]" in place of the personal details.</div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Live trip alerts: incident radius (km)</label>
                                    <input type="number" min="0.2" max="50" step="0.1" value={alertRadiusKm} onChange={e => setAlertRadiusKm(e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Off-itinerary distance (km)</label>
                                    <input type="number" min="1" max="500" value={offItineraryKm} onChange={e => setOffItineraryKm(e.target.value)} />
                                </div>
                                <div className="col-span-2 text-xs text-dim">Defaults for principal trips that have live alerts switched on (each trip can override). An alert is raised when the principal's consented tracker is within the radius of a geo-tagged incident, or further than the off-itinerary distance from every trip location, during the trip dates only.</div>
                                <div className="col-span-2 text-xs text-dim">ACLED conflict data is used for risk evidence and live alerts only when <span className="font-mono">ACLED_API_KEY</span> and <span className="font-mono">ACLED_EMAIL</span> are set in the server environment. ACLED requires a commercial licence for client work: do not set them without one.</div>
                            </div>
                            <div className="flex justify-end">
                                <button className="btn-gold" onClick={saveTenant} disabled={savingTenant}>
                                    {savingTenant ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Team — admin only */}
                    {isAdmin && (
                        <div className="panel mb-6">
                            <div className="panel-header flex justify-between items-center flex-wrap gap-2">
                                <div>
                                    <div className="font-display text-xl gold">Team</div>
                                    <div className="text-xs font-mono text-muted mt-1">
                                        Your tenant's analysts — invite, change role, activate or deactivate.
                                    </div>
                                </div>
                                <button className="btn-gold" onClick={() => { setInviteError(null); setInviteOpen(true); }}>
                                    + Invite Analyst
                                </button>
                            </div>
                            <div className="px-6 py-5">
                                {teamError && <div className="text-xs mb-3" style={{color: 'var(--red)'}}>{teamError}</div>}
                                {teamLoading ? (
                                    <div className="text-sm text-muted">Loading…</div>
                                ) : team.length === 0 ? (
                                    <div className="text-sm text-muted">No analysts yet.</div>
                                ) : (
                                    <div style={{overflowX: 'auto'}}>
                                        <table className="w-full text-sm" style={{borderCollapse: 'collapse'}}>
                                            <thead>
                                                <tr className="text-left text-xs font-mono uppercase tracking-widest text-muted">
                                                    <th style={{padding: '8px 8px 8px 0'}}>Name</th>
                                                    <th style={{padding: '8px'}}>Email</th>
                                                    <th style={{padding: '8px'}}>Role</th>
                                                    <th style={{padding: '8px'}}>Status</th>
                                                    <th style={{padding: '8px'}}>Last seen</th>
                                                    <th style={{padding: '8px', textAlign: 'right'}}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {team.map(a => {
                                                    const busy = rowBusyId === a.id;
                                                    // A role option is selectable if it's grantable by me, or it's
                                                    // this row's current role (so an out-of-range role still shows).
                                                    const roleOptions = grantableRoles.includes(a.role)
                                                        ? grantableRoles : [a.role, ...grantableRoles];
                                                    return (
                                                        <tr key={a.id} style={{borderTop: '1px solid var(--rule)'}}>
                                                            <td style={{padding: '10px 8px 10px 0'}}>
                                                                {a.display_name}
                                                                {a.is_system && <span className="text-muted text-xs ml-2">(system)</span>}
                                                                {a.is_self && <span style={{color: 'var(--gold)'}} className="text-xs ml-2">(you)</span>}
                                                            </td>
                                                            <td style={{padding: '10px 8px', color: 'var(--cyan)'}}>{a.email}</td>
                                                            <td style={{padding: '10px 8px'}}>
                                                                <select
                                                                    value={a.role}
                                                                    disabled={busy || a.is_system}
                                                                    onChange={e => changeRole(a, e.target.value)}
                                                                    style={{background: 'var(--zone-3)', color: 'var(--text)', border: '1px solid var(--rule-2)', padding: '4px 8px', fontSize: '12px'}}>
                                                                    {roleOptions.map(r => (
                                                                        <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td style={{padding: '10px 8px', color: a.is_active ? 'var(--green)' : 'var(--text-muted)'}}>
                                                                {a.is_active ? 'active' : 'inactive'}
                                                            </td>
                                                            <td style={{padding: '10px 8px', color: 'var(--text-muted)', fontSize: '12px'}} title={a.last_seen_at || ''}>
                                                                {a.last_seen_at ? new Date(a.last_seen_at).toLocaleString('en-GB') : 'never'}
                                                            </td>
                                                            <td style={{padding: '10px 0', textAlign: 'right'}}>
                                                                {!a.is_system && !a.is_self && (
                                                                    <button
                                                                        className="btn-gold"
                                                                        disabled={busy}
                                                                        onClick={() => toggleActive(a)}
                                                                        style={{padding: '4px 10px', fontSize: '11px', background: 'transparent',
                                                                                color: a.is_active ? 'var(--red)' : 'var(--green)',
                                                                                border: `1px solid ${a.is_active ? 'var(--red)' : 'var(--green)'}`}}>
                                                                        {a.is_active ? 'Deactivate' : 'Activate'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="text-xs text-muted mt-4" style={{lineHeight: 1.5}}>
                                    You can only manage your own tenant's team, can't grant a role above your own
                                    ({ROLE_LABEL[myRole] || myRole}), and can't deactivate yourself. "Last seen" is
                                    the most recent action recorded for that analyst — it's blank for someone who
                                    hasn't done anything yet, not necessarily someone who has never logged in.
                                </div>
                            </div>
                        </div>
                    )}

                    {inviteOpen && (
                        <Dialog title="Invite Analyst" onClose={() => !inviteBusy && setInviteOpen(false)}>
                            <form onSubmit={invite} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Full Name</label>
                                    <input value={inviteForm.full_name} disabled={inviteBusy} autoFocus
                                           onChange={e => setInviteForm({...inviteForm, full_name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Email</label>
                                    <input type="email" value={inviteForm.email} disabled={inviteBusy}
                                           onChange={e => setInviteForm({...inviteForm, email: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Role</label>
                                    <select value={inviteForm.role} disabled={inviteBusy}
                                            onChange={e => setInviteForm({...inviteForm, role: e.target.value})}
                                            style={{width: '100%', background: 'var(--zone-3)', color: 'var(--text)', border: '1px solid var(--rule-2)', padding: '10px 12px', fontSize: '14px'}}>
                                        {grantableRoles.map(r => (
                                            <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
                                        ))}
                                    </select>
                                </div>
                                {inviteError && <div className="text-xs" style={{color: 'var(--red)'}}>{inviteError}</div>}
                                <div className="flex gap-3 justify-end">
                                    <button type="button" className="btn-ghost" disabled={inviteBusy} onClick={() => setInviteOpen(false)}>Cancel</button>
                                    <button type="submit" className="btn-gold" disabled={inviteBusy}>
                                        {inviteBusy ? 'Sending…' : 'Send Invitation'}
                                    </button>
                                </div>
                            </form>
                        </Dialog>
                    )}

                    {/* Outbound email status + test */}
                    <div className="panel mb-6">
                        <div className="panel-header">
                            <div className="font-display text-xl gold">Outbound Email</div>
                            <div className="text-xs font-mono text-muted mt-1">
                                Microsoft Graph via OAuth2 — configured server-side via .env. This page lets you validate the config and trigger a manual drain.
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Status</div>
                                    <div className="font-mono" style={{
                                        color: smtp.ready ? 'var(--green)' : 'var(--amber)'
                                    }}>
                                        {smtp.ready ? 'READY' : 'NOT CONFIGURED'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Transport</div>
                                    <div className="text-xs font-mono">{smtp.host || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">From</div>
                                    <div className="text-xs font-mono">
                                        {smtp.from_address ? `${smtp.from_name} <${smtp.from_address}>` : '—'}
                                    </div>
                                </div>
                            </div>

                            {!smtp.ready && (
                                <div className="text-xs text-muted p-3" style={{background: 'rgba(201,169,110,0.05)', border: '1px solid rgba(201,169,110,0.15)'}}>
                                    <div className="mb-2">To enable: register an Azure AD app with <code className="font-mono">Mail.Send</code> permission, then set in .env and restart uvicorn:</div>
                                    <code className="font-mono">SMTP_ENABLED=true</code><br/>
                                    <code className="font-mono">SMTP_FROM_ADDRESS=alerts@obsidianarc.co.uk</code><br/>
                                    <code className="font-mono">SMTP_FROM_NAME=Obsidian Veil</code><br/>
                                    <code className="font-mono">GRAPH_TENANT_ID=&lt;your Azure AD tenant id&gt;</code><br/>
                                    <code className="font-mono">GRAPH_CLIENT_ID=&lt;app registration client id&gt;</code><br/>
                                    <code className="font-mono">GRAPH_CLIENT_SECRET=&lt;client secret VALUE (not the ID)&gt;</code>
                                </div>
                            )}

                            {smtp.ready && (
                                <>
                                    <div>
                                        <label className="block text-xs font-mono uppercase tracking-widest text-muted mb-2">Send test email to</label>
                                        <div className="flex gap-2">
                                            <input className="flex-1" value={testTo} onChange={e => setTestTo(e.target.value)} />
                                            <button className="btn-gold" onClick={sendTest} disabled={testing}>
                                                {testing ? 'Sending…' : 'Send test'}
                                            </button>
                                        </div>
                                        {testResult && (
                                            <div className="text-xs font-mono mt-2" style={{
                                                color: testResult.sent ? 'var(--green)' : 'var(--red)'
                                            }}>
                                                {testResult.sent ? `Sent to ${testResult.to}` : `Failed: ${testResult.reason}`}
                                            </div>
                                        )}
                                    </div>

                                    <div className="pt-4 border-t border-veil">
                                        <div className="text-xs font-mono uppercase tracking-widest text-muted mb-2">
                                            Manual drain
                                        </div>
                                        <div className="text-xs text-muted mb-3">
                                            Send any queued notifications immediately, without waiting for the next scheduled run.
                                        </div>
                                        <button className="btn-ghost" onClick={drainNow} disabled={draining}>
                                            {draining ? 'Draining…' : 'Drain queue now'}
                                        </button>
                                        {drainResult && (
                                            <div className="text-xs font-mono mt-3">
                                                {drainResult.sent !== undefined ?
                                                    <span style={{color: 'var(--green)'}}>
                                                        {drainResult.sent} sent · {drainResult.failed} failed · {drainResult.skipped} skipped
                                                        {drainResult.reason && ` · ${drainResult.reason}`}
                                                    </span> :
                                                    <span style={{color: 'var(--red)'}}>{drainResult.reason}</span>}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

export { SettingsView };
